// src/utils/productWiseSalesExport.js
import ExcelJS from 'exceljs';
import { createDoc, buildTheme, drawBanner, drawTwoColCards, drawItemsTable, formatCurrency } from './offlineOrderPdfExporter';

const HEADER_COLOR = 'FF667EEA';

const COLUMNS = [
  { key: 'productName', header: 'Title', width: 34 },
  { key: 'itemCount', header: 'Item Count', width: 12, numFmt: '#,##0' },
  { key: 'amount', header: 'Amount', width: 16, numFmt: '#,##0.00' },
  { key: 'contribution', header: 'Contribution (%)', width: 16, numFmt: '0.00' },
  { key: 'discount', header: 'Discount', width: 16, numFmt: '#,##0.00' },
  { key: 'amountAfterDiscount', header: 'Amount After Discount', width: 22, numFmt: '#,##0.00' },
  { key: 'amountPerItem', header: 'Amount per Item', width: 16, numFmt: '#,##0.00' },
  { key: 'discountPerItem', header: 'Discount per Item', width: 17, numFmt: '#,##0.00' },
  { key: 'discountPctPerItem', header: 'Discount % per Item', width: 18, numFmt: '0.00' },
];

const SUMMARY_ROWS = [
  ['Total Order Count', 'orderCount', '#,##0'],
  ['Total Order Amount', 'orderAmount', '#,##0.00'],
  ['Total Order Discount', 'orderDiscount', '#,##0.00'],
  ['Total Product Count', 'productCount', '#,##0'],
  ['Total Product Amount', 'productAmount', '#,##0.00'],
  ['Total Product Discount', 'productDiscount', '#,##0.00'],
];

const fileBase = ({ dateFrom, dateTo }) => `product-wise-sales-${dateFrom}_${dateTo}`;

const fmt2 = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/**
 * @param {Object} data
 * @param {Array} data.rows
 * @param {Object} data.summary
 * @param {Object} data.meta - { dateFrom, dateTo, shopLabel, channelLabel }
 */
export const exportProductWiseSalesExcel = async ({ rows, summary, meta }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Rettzy';
  workbook.created = new Date();
  const sheet = workbook.addWorksheet('Product Wise Sales');

  sheet.columns = COLUMNS.map(({ key, width }) => ({ key, width }));

  sheet.addRow(['Product Wise Sales']).font = { bold: true, size: 14 };
  sheet.addRow([`Dispatched Date: ${meta.dateFrom} to ${meta.dateTo}`]);
  sheet.addRow([`Shops: ${meta.shopLabel}`]);
  if (meta.channelLabel) sheet.addRow([`Channels: ${meta.channelLabel}`]);
  sheet.addRow([]);

  SUMMARY_ROWS.forEach(([label, key, numFmt]) => {
    const row = sheet.addRow([label, summary[key] || 0]);
    row.getCell(1).font = { bold: true };
    row.getCell(2).numFmt = numFmt;
  });
  sheet.addRow([]);

  const headerRow = sheet.addRow(COLUMNS.map((c) => c.header));
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_COLOR } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
  });
  headerRow.height = 22;

  rows.forEach((r) => {
    const row = sheet.addRow(COLUMNS.map((c) => r[c.key] ?? null));
    COLUMNS.forEach((c, i) => {
      if (c.numFmt) row.getCell(i + 1).numFmt = c.numFmt;
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${fileBase(meta)}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
};

/**
 * @param {Object} data - same shape as exportProductWiseSalesExcel
 * @param {Object} themeColors - { primary, primaryDark } hex
 */
export const exportProductWiseSalesPDF = ({ rows, summary, meta }, themeColors) => {
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);

  drawBanner(doc, {
    pageW, ml, mr, theme,
    title: 'Product Wise Sales',
    metaLine: `Dispatched: ${meta.dateFrom} to ${meta.dateTo}`,
    badgeLabel: 'SALES REPORT',
    dateText: `Shops: ${meta.shopLabel}${meta.channelLabel ? ` · ${meta.channelLabel}` : ''}`.slice(0, 80),
  });

  const y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Orders',
      lines: [
        `Order Count: ${(summary.orderCount || 0).toLocaleString('en-US')}`,
        `Order Amount: ${formatCurrency(summary.orderAmount)}`,
        `Order Discount: ${formatCurrency(summary.orderDiscount)}`,
      ],
    },
    right: {
      title: 'Products',
      lines: [
        `Product Count: ${(summary.productCount || 0).toLocaleString('en-US')}`,
        `Product Amount: ${formatCurrency(summary.productAmount)}`,
        `Product Discount: ${formatCurrency(summary.productDiscount)}`,
      ],
    },
  });

  drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['Title', 'Qty', 'Amount', 'Contr. %', 'Discount', 'After Disc.', 'Amt / Item', 'Disc / Item', 'Disc %'],
    body: rows.map((r) => [
      r.productName,
      (r.itemCount || 0).toLocaleString('en-US'),
      fmt2(r.amount),
      fmt2(r.contribution),
      fmt2(r.discount),
      fmt2(r.amountAfterDiscount),
      fmt2(r.amountPerItem),
      fmt2(r.discountPerItem),
      fmt2(r.discountPctPerItem),
    ]),
    footerLabel: `Product Wise Sales · ${meta.dateFrom} to ${meta.dateTo}`,
  });

  doc.save(`${fileBase(meta)}.pdf`);
};
